import { Tables } from '@/supabase/types'

import { LLMID } from '@/types/llms'
import {
  canCreateSpaceObject,
  getAttachmentLimitConfig,
  getBusinessPlanFromLookupKey,
  getBusinessPlanIdFromInternalNexusPlan,
  getEffectiveFeaturePlanId,
  hasStudioEntitlement,
  isLegacyByokPlan,
  isBusinessPlanId,
  normalizePersonalPlanTier,
  type LosiAttachmentLimitConfig,
  type LosiPublicPlanId,
  type LosiSpaceObjectType
} from '@/lib/billing/losi-pricing'
import {
  canCreateWorkspaceSpaceObject,
  getWorkspaceSponsoredFeaturePlanId,
  type WorkspaceSponsorProfile
} from '@/lib/billing/workspace-entitlements'
import { getEnvInt } from '@/lib/env'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { PLAN_FREE, PLAN_PRO, PLAN_ULTRA, PLAN_PLUS } from '@/lib/stripe/config'
import {
  getNexusPlanLimits,
  isNexusPlan,
  NEXUS_PLAN_STARTER,
  NEXUS_PLAN_PROFESSIONAL,
  NEXUS_PLAN_ENTERPRISE,
  NEXUS_PRICING
} from '@/lib/stripe/nexus-config'
import { isSuperAdmin } from '@/lib/utils'

export const FREE_MESSAGE_DAILY_LIMIT = getEnvInt('FREE_MESSAGE_DAILY_LIMIT', 30)
export const PLUS_MESSAGE_DAILY_LIMIT = getEnvInt('PLUS_MESSAGE_DAILY_LIMIT', 50)
export const PRO_MESSAGE_DAILY_LIMIT = getEnvInt('PRO_MESSAGE_DAILY_LIMIT', 50)
export const CATCHALL_MESSAGE_DAILY_LIMIT = getEnvInt('CATCHALL_MESSAGE_DAILY_LIMIT', 300)
export const CHAT_PAYG_MESSAGE_CREDIT_COST = getEnvInt('CHAT_PAYG_MESSAGE_CREDIT_COST', 1)

export const PLUS_ULTRA_MESSAGE_WEEKLY_LIMIT = getEnvInt('PLUS_ULTRA_MESSAGE_WEEKLY_LIMIT', 5)

export const PRO_ULTRA_MESSAGE_DAILY_LIMIT = getEnvInt('PRO_ULTRA_MESSAGE_DAILY_LIMIT', 5)

export const ULTRA_MESSAGE_DAILY_LIMIT = getEnvInt('ULTRA_MESSAGE_DAILY_LIMIT', 50)

export const FREE_LOSI_MAX_MESSAGE_DAILY_LIMIT = getEnvInt(
  'FREE_LOSI_MAX_MESSAGE_DAILY_LIMIT',
  25
)
export const FREE_LOSI_MAX_MESSAGE_WINDOW_LIMIT = getEnvInt(
  'FREE_LOSI_MAX_MESSAGE_WINDOW_LIMIT',
  10
)
export const FREE_LOSI_MAX_MESSAGE_WINDOW_HOURS = getEnvInt(
  'FREE_LOSI_MAX_MESSAGE_WINDOW_HOURS',
  3
)
export const FREE_LOSI_MAX_MESSAGE_WINDOW_MS =
  FREE_LOSI_MAX_MESSAGE_WINDOW_HOURS * 60 * 60 * 1000

export const FREE_IMAGE_DAILY_LIMIT = 5

// Video generation monthly limits
export const FREE_VIDEO_MONTHLY_LIMIT = 2
export const PRO_VIDEO_480P_MONTHLY_LIMIT = 10
export const PRO_VIDEO_720P_MONTHLY_LIMIT = 5
export const ULTRA_VIDEO_480P_MONTHLY_LIMIT = 20
export const ULTRA_VIDEO_720P_MONTHLY_LIMIT = 10
export const ULTRA_VIDEO_1080P_MONTHLY_LIMIT = 5

// Studio models - pay as you go (no monthly limits)
// Keep legacy MiniMax/Luma ids + current Replicate leaders (Veo/Kling/Seedance/P-Video/…)
export const STUDIO_VIDEO_MODELS = [
  // Current Replicate leaders
  'google/veo-3.1-fast',
  'google/veo-3.1',
  'runwayml/gen-4.5',
  'kwaivgi/kling-v3-video',
  'kwaivgi/kling-v3-omni-video',
  'kwaivgi/kling-v4.0',
  'bytedance/seedance-2.0',
  'bytedance/seedance-2.0-fast',
  'prunaai/p-video',
  'prunaai/p-video-2-pro',
  'prunaai/p-video-2-pro-reference',
  'prunaai/p-video-animate',
  'minimax/hailuo-2.3',
  'minimax/hailuo-2.3-fast',
  'luma/ray-3.2',
  'luma/modify-video',
  'wan-video/wan-2.7-i2v',
  'pixverse/pixverse-v6-max',
  // Legacy Studio ids (still billed)
  'luma/ray',
  'minimax/video-01',
  'minimax/video-01-director',
  'minimax/video-01-live',
  'minimax/music-01'
]

export const ALLOWED_USERS = process.env.NEXT_PUBLIC_ALLOWED_USERS?.split(',') || []
export const ALLOWED_MODELS = process.env.NEXT_PUBLIC_ALLOWED_MODELS?.split(',') || []
const ACTIVE_NEXUS_STATUSES = ['active', 'trialing'] as const

const LOSI_PLAN_DISPLAY_NAMES: Record<LosiPublicPlanId, string> = {
  free: 'Free',
  plus: 'Plus',
  pro: 'Pro',
  ultra: 'Ultra',
  business_starter: 'Business Starter',
  business_pro: 'Business Pro',
  business_ultra: 'Business Ultra',
  enterprise: 'Enterprise'
}

const SPACE_OBJECT_LABELS: Record<LosiSpaceObjectType, string> = {
  chat: 'chats',
  note: 'notes',
  folder: 'folders',
  task: 'tasks',
  event: 'events',
  sprint: 'sprints',
  sheet: 'sheets',
  whiteboard: 'whiteboards',
  design: 'designs'
}

const SPACE_OBJECT_REQUIRED_PLAN: Record<LosiSpaceObjectType, LosiPublicPlanId> = {
  chat: 'free',
  note: 'free',
  folder: 'free',
  task: 'free',
  event: 'free',
  sprint: 'plus',
  sheet: 'pro',
  whiteboard: 'pro',
  design: 'pro'
}

export type SpaceInviteRole = 'viewer' | 'commenter' | 'editor' | 'admin'
export type SpaceCollaboratorItemType = 'note' | 'task'

export const SPACE_UPGRADE_PAYWALL_DELAY_MS = 2500

export type EffectiveLosiPlanProfile = Pick<
  Tables<'profiles'>,
  'plan' | 'nexus_plan' | 'nexus_plan_status'
>

const SPACE_INVITE_ALLOWED_ROLES_BY_PLAN: Record<
  LosiPublicPlanId,
  readonly SpaceInviteRole[]
> = {
  free: ['viewer'],
  plus: ['viewer'],
  pro: ['viewer', 'commenter', 'editor', 'admin'],
  ultra: ['viewer', 'commenter', 'editor', 'admin'],
  business_starter: ['viewer', 'commenter', 'editor', 'admin'],
  business_pro: ['viewer', 'commenter', 'editor', 'admin'],
  business_ultra: ['viewer', 'commenter', 'editor', 'admin'],
  enterprise: ['viewer', 'commenter', 'editor', 'admin']
}

const SPACE_ITEM_INVITE_ALLOWED_ROLES_BY_PLAN: Record<
  SpaceCollaboratorItemType,
  Record<LosiPublicPlanId, readonly SpaceInviteRole[]>
> = {
  note: {
    free: ['viewer'],
    plus: ['viewer', 'editor'],
    pro: ['viewer', 'commenter', 'editor', 'admin'],
    ultra: ['viewer', 'commenter', 'editor', 'admin'],
    business_starter: ['viewer', 'commenter', 'editor', 'admin'],
    business_pro: ['viewer', 'commenter', 'editor', 'admin'],
    business_ultra: ['viewer', 'commenter', 'editor', 'admin'],
    enterprise: ['viewer', 'commenter', 'editor', 'admin']
  },
  task: {
    free: ['viewer'],
    plus: ['viewer'],
    pro: ['viewer', 'commenter', 'editor', 'admin'],
    ultra: ['viewer', 'commenter', 'editor', 'admin'],
    business_starter: ['viewer', 'commenter', 'editor', 'admin'],
    business_pro: ['viewer', 'commenter', 'editor', 'admin'],
    business_ultra: ['viewer', 'commenter', 'editor', 'admin'],
    enterprise: ['viewer', 'commenter', 'editor', 'admin']
  }
}

type AttachmentUploadCandidate = {
  size: number
}

export type PlanGatedTool = {
  badge?: unknown
} | null | undefined

type AttachmentUploadValidationResult =
  | {
      ok: true
      planId: LosiPublicPlanId
      limits: LosiAttachmentLimitConfig
    }
  | {
      ok: false
      planId: LosiPublicPlanId
      limits: LosiAttachmentLimitConfig
      message: string
    }

function hasStudioAccess(profile: Tables<'profiles'> | null) {
  return hasStudioEntitlement(profile)
}

export function validatePlanForModel(profile: Tables<'profiles'> | null, model?: LLMID) {
  if (!model) {
    return false
  }

  // openrouter models are always allowed
  if (model.includes('/')) {
    // Studio models require the shared Studio/Nexus Business entitlement.
    if (STUDIO_VIDEO_MODELS.includes(model)) {
      return hasStudioAccess(profile)
    }
    return true
  }

  const rawPlan = profile?.plan || ''
  const userPlan = normalizePersonalPlanTier(getEffectiveFeaturePlanId(profile))

  if (isLegacyByokPlan(rawPlan)) {
    return true
  }

  const modelData = LLM_LIST.find((x) => x.modelId === model || x.hostedId === model)
  if (!modelData) {
    return false
  }

  if (ALLOWED_MODELS.includes(model)) {
    return true
  }

  if (modelData.tier === 'free' || modelData.tier === undefined) {
    return true
  }

  if (!profile) {
    return false
  }

  // Free users get first 5 messages with Losi Max Auto (then silently switched to losi-omega)
  if (model === 'losi-max-auto' && userPlan === PLAN_FREE) {
    return true // Server-side will enforce the 5 message limit and switch
  }

  // Allow limited free usage for Losi Max (losi-omega); server enforces limits
  if (model === 'losi-omega' && userPlan === PLAN_FREE) {
    return true
  }

  // Losi Mini is completely free for everyone
  if (model === 'losi-mini') {
    return true
  }

  // Plus plan includes access to Losi Max (pro-tier) and other pro-tier models per limits
  if (
    userPlan === PLAN_ULTRA ||
    userPlan === PLAN_PRO ||
    userPlan === PLAN_PLUS
  )
    return true

  return false
}

export function validatePlanForAssistant(
  profile: Tables<'profiles'> | null,
  assistant: Tables<'assistants'>
) {
  return validatePlanForModel(profile, assistant.model as LLMID)
}

export function validatePlanForTools(
  profile: Tables<'profiles'> | null,
  tools: readonly PlanGatedTool[],
  model?: LLMID
) {
  // Model must be allowed when specified
  if (model && !validatePlanForModel(profile, model)) {
    return false
  }

  // Missing profile -> only free tools allowed
  if (!profile) return tools.every((tool) => !tool?.badge)

  const userPlanRaw = profile.plan || 'free'
  const userPlan = normalizePersonalPlanTier(getEffectiveFeaturePlanId(profile))

  // BYOK bypass
  if (isLegacyByokPlan(userPlanRaw)) return true

  // Check each tool against plan
  return tools.every((tool) => {
    const badge = (tool?.badge || '').toString().toLowerCase()
    if (!badge) return true // no badge = available to all

    if (badge === 'pro') {
      // Plus users should have access to Pro-badge tools
      return ['plus', 'pro', 'ultra'].includes(userPlan)
    }

    if (badge === 'ultra' || badge === 'super') {
      return ['ultra'].includes(userPlan)
    }

    if (badge === 'studio') {
      return hasStudioAccess(profile)
    }

    // Unknown badges default to requiring paid
    return userPlan !== 'free'
  })
}

// Check if user is on free plan
export function isFreePlan(profile: Tables<'profiles'> | null): boolean {
  if (!profile) return true

  const rawPlan = (profile.plan || '').toLowerCase()
  if (
    rawPlan === 'enterprise' ||
    isBusinessPlanId(rawPlan as LosiPublicPlanId) ||
    getBusinessPlanIdFromInternalNexusPlan(rawPlan) ||
    getBusinessPlanFromLookupKey(rawPlan)
  ) {
    return false
  }

  return getEffectiveFeaturePlanId(profile) === PLAN_FREE
}

export function getLosiPlanDisplayName(planId: LosiPublicPlanId): string {
  return LOSI_PLAN_DISPLAY_NAMES[planId]
}

export function getRequiredSpaceObjectPlanId(
  objectType: LosiSpaceObjectType
): LosiPublicPlanId {
  return SPACE_OBJECT_REQUIRED_PLAN[objectType]
}

export function getEffectiveLosiPlanId(
  profile: EffectiveLosiPlanProfile | null
): LosiPublicPlanId {
  return getEffectiveFeaturePlanId(profile)
}

export function canCreateSpaceItem(
  profile: Tables<'profiles'> | null,
  objectType: LosiSpaceObjectType
): boolean {
  return canCreateSpaceObject(getEffectiveLosiPlanId(profile), objectType)
}

export function getWorkspaceScopedLosiPlanId(
  profile: EffectiveLosiPlanProfile | null,
  workspaceSponsorProfile?: WorkspaceSponsorProfile | null
): LosiPublicPlanId {
  return getWorkspaceSponsoredFeaturePlanId(profile, workspaceSponsorProfile)
}

export function canCreateWorkspaceSpaceItem(
  profile: EffectiveLosiPlanProfile | null,
  objectType: LosiSpaceObjectType,
  workspaceSponsorProfile?: WorkspaceSponsorProfile | null
): boolean {
  return canCreateWorkspaceSpaceObject(
    profile,
    objectType,
    workspaceSponsorProfile
  )
}

export function getSpaceObjectUpgradeMessage(
  profile: Tables<'profiles'> | null,
  objectType: LosiSpaceObjectType
): string {
  const requiredPlanId = SPACE_OBJECT_REQUIRED_PLAN[objectType]
  const requiredPlanName = getLosiPlanDisplayName(requiredPlanId)
  const objectLabel = SPACE_OBJECT_LABELS[objectType]

  if (requiredPlanId === 'free') {
    return `Spaces already include ${objectLabel}.`
  }

  if (requiredPlanId === PLAN_PLUS) {
    if (objectType === 'event') {
      return 'Plus adds events when work needs dates, deadlines, and a calendar.'
    }

    if (objectType === 'sprint') {
      return 'Plus adds sprints when work needs a focused project cycle.'
    }

    return `Plus gives you more room for ${objectLabel} as your work grows.`
  }

  return `${requiredPlanName} adds ${objectLabel} for more structured work inside each Space.`
}

export function scheduleDelayedPaywall(
  openPaywall: () => void,
  delayMs = SPACE_UPGRADE_PAYWALL_DELAY_MS
) {
  if (typeof window === 'undefined') {
    return null
  }

  return window.setTimeout(() => {
    openPaywall()
  }, delayMs)
}

export function getAllowedSpaceInviteRoles(
  profile: Tables<'profiles'> | null
): readonly SpaceInviteRole[] {
  return SPACE_INVITE_ALLOWED_ROLES_BY_PLAN[getEffectiveLosiPlanId(profile)]
}

export function canAssignSpaceInviteRole(
  profile: Tables<'profiles'> | null,
  role: SpaceInviteRole
): boolean {
  return getAllowedSpaceInviteRoles(profile).includes(role)
}

export function getSpaceInviteUpgradeMessage(
  profile: Tables<'profiles'> | null,
  role: SpaceInviteRole
): string {
  if (canAssignSpaceInviteRole(profile, role)) {
    return 'Your plan already supports that Space role.'
  }

  return 'Pro unlocks commenter, editor, and admin invites in Spaces. Free and Plus can invite viewers only.'
}

export function getAllowedSpaceItemInviteRoles(
  profile: EffectiveLosiPlanProfile | null,
  itemType: SpaceCollaboratorItemType
): readonly SpaceInviteRole[] {
  return SPACE_ITEM_INVITE_ALLOWED_ROLES_BY_PLAN[itemType][
    getEffectiveLosiPlanId(profile)
  ]
}

export function canAssignSpaceItemInviteRole(
  profile: EffectiveLosiPlanProfile | null,
  itemType: SpaceCollaboratorItemType,
  role: SpaceInviteRole
): boolean {
  return getAllowedSpaceItemInviteRoles(profile, itemType).includes(role)
}

export function getSpaceItemInviteUpgradeMessage(
  profile: EffectiveLosiPlanProfile | null,
  itemType: SpaceCollaboratorItemType,
  role: SpaceInviteRole
): string {
  if (canAssignSpaceItemInviteRole(profile, itemType, role)) {
    return 'Your plan already supports that item role.'
  }

  if (itemType === 'note') {
    return 'Plus unlocks note editor invites. Pro unlocks commenter, admin, and full note and task collaboration roles.'
  }

  return 'Pro unlocks commenter, editor, and admin invites for task items. Free and Plus can invite viewers only to tasks.'
}

export function getAttachmentUploadLimitForPlan(
  planId: LosiPublicPlanId
): LosiAttachmentLimitConfig {
  return getAttachmentLimitConfig(planId)
}

export function getAttachmentUploadLimit(
  profile: EffectiveLosiPlanProfile | null
): LosiAttachmentLimitConfig {
  return getAttachmentUploadLimitForPlan(getEffectiveLosiPlanId(profile))
}

function validateAttachmentUploadWithPlan(
  planId: LosiPublicPlanId,
  files: readonly AttachmentUploadCandidate[]
): AttachmentUploadValidationResult {
  const limits = getAttachmentUploadLimitForPlan(planId)

  if (
    typeof limits.filesPerUpload === 'number' &&
    files.length > limits.filesPerUpload
  ) {
    return {
      ok: false,
      planId,
      limits,
      message: `${getLosiPlanDisplayName(planId)} allows up to ${limits.filesPerUploadLabel} files per upload. Upgrade for larger batches.`
    }
  }

  const maxAttachmentSizeBytes = limits.maxAttachmentSizeBytes

  if (typeof maxAttachmentSizeBytes === 'number') {
    const oversizeFile = files.find(
      (file) => file.size > maxAttachmentSizeBytes
    )

    if (oversizeFile) {
      return {
        ok: false,
        planId,
        limits,
        message: `${getLosiPlanDisplayName(planId)} allows attachments up to ${limits.maxAttachmentSizeLabel}. Upgrade for larger uploads.`
      }
    }
  }

  return {
    ok: true,
    planId,
    limits
  }
}

export function validateAttachmentUploadForPlan(
  planId: LosiPublicPlanId,
  files: readonly AttachmentUploadCandidate[]
): AttachmentUploadValidationResult {
  return validateAttachmentUploadWithPlan(planId, files)
}

export function validateAttachmentUpload(
  profile: EffectiveLosiPlanProfile | null,
  files: readonly AttachmentUploadCandidate[]
): AttachmentUploadValidationResult {
  return validateAttachmentUploadWithPlan(
    getEffectiveLosiPlanId(profile),
    files
  )
}

// Check if user can use GPT-5 reasoning controls
export function canUseGPT5Reasoning(profile: Tables<'profiles'> | null): boolean {
  if (!profile) return false
  const userPlan = normalizePersonalPlanTier(getEffectiveFeaturePlanId(profile))
  return userPlan === PLAN_PRO || userPlan === PLAN_ULTRA
}

// New function to check if a model is a studio model (pay-as-you-go)
export function isStudioModel(model: string) {
  return STUDIO_VIDEO_MODELS.includes(model)
}

// New function to validate user has enough credits for a studio model
export async function validateCreditsForStudioModel(
  profile: Tables<'profiles'> | null,
  model: string
): Promise<boolean> {
  if (!profile || !profile.user_id || !isStudioModel(model)) {
    return false
  }

  try {
    // This would call a server function to check credits
    // Implementation would be in a server action
    return true
  } catch (error) {
    console.error('Error checking credits for studio model:', error)
    return false
  }
}

// Nexus subscription functions
export function hasActiveNexusSubscription(profile: Tables<"profiles"> | null): boolean {
  if (!profile) return false

  // Handle both old format ('starter') and new format ('nexus_starter')
  const plan = profile.nexus_plan
  const validPlans = [
    NEXUS_PLAN_STARTER, NEXUS_PLAN_PROFESSIONAL, NEXUS_PLAN_ENTERPRISE,
    'starter', 'professional', 'enterprise' // backward compatibility
  ]

  return (
    ACTIVE_NEXUS_STATUSES.includes(
      (profile.nexus_plan_status ?? '') as (typeof ACTIVE_NEXUS_STATUSES)[number]
    ) &&
    plan &&
    validPlans.includes(plan)
  )
}

/** Platform/super admins can use Nexus integration flows without a subscription. */
export function canBypassNexusSubscription(
  profile: Tables<"profiles"> | null,
  userEmail?: string | null
): boolean {
  if (!profile) return false
  if (userEmail && isSuperAdmin(userEmail)) return true
  const adminFlags = profile as unknown as { is_platform_admin?: boolean | null; is_admin?: boolean | null }
  return adminFlags.is_platform_admin === true || adminFlags.is_admin === true
}

// Get Nexus plan tier for a user
export function getNexusPlanTier(profile: Tables<"profiles"> | null): string | null {
  if (!profile || !hasActiveNexusSubscription(profile)) return null
  return profile.nexus_plan
}

// Validate if user can access Nexus features
export function canAccessNexusFeature(
  profile: Tables<"profiles"> | null,
  feature: keyof typeof NEXUS_PRICING.starter
): boolean {
  if (!profile || !hasActiveNexusSubscription(profile)) return false

  const planLimits = getNexusPlanLimits(profile.nexus_plan || '')
  if (!planLimits) return false

  // For boolean features, check if they exist and are true
  const featureValue = planLimits[feature]
  if (typeof featureValue === 'boolean') {
    return featureValue
  }

  // For numeric/string features, just check if the plan has access
  return true
}

export function getNexusPricing(planType: string) {
  switch (planType) {
    case NEXUS_PLAN_STARTER:
      return NEXUS_PRICING.starter
    case NEXUS_PLAN_PROFESSIONAL:
      return NEXUS_PRICING.professional
    case NEXUS_PLAN_ENTERPRISE:
      return NEXUS_PRICING.enterprise
    default:
      return null
  }
}

// Validate Nexus integration access
export function canAccessNexusIntegration(
  profile: Tables<"profiles"> | null,
  integrationCount: number
): boolean {
  if (!profile || !hasActiveNexusSubscription(profile)) return false

  const planLimits = getNexusPlanLimits(profile.nexus_plan || '')
  if (!planLimits) return false

  const maxIntegrations = planLimits.integrations
  return integrationCount <= maxIntegrations
}

// Validate Nexus agent creation
export function canCreateNexusAgent(
  profile: Tables<"profiles"> | null,
  currentAgentCount: number,
  userEmail?: string | null
): boolean {
  if (!profile) return false

  // Super admins can create unlimited agents, even without a Nexus subscription.
  // Check multiple admin indicators:
  // 1. Email-based super admin check
  if (userEmail && isSuperAdmin(userEmail)) return true

  type ProfileAdminFlags = {
    is_platform_admin?: boolean | null
    is_admin?: boolean | null
  }

  const adminFlags = profile as unknown as ProfileAdminFlags

  // 2. Platform admin field in profile
  if (adminFlags.is_platform_admin === true) return true

  // 3. Admin field in profile (used in middleware)
  if (adminFlags.is_admin === true) return true

  if (!hasActiveNexusSubscription(profile)) return false

  const planLimits = getNexusPlanLimits(profile.nexus_plan || '')
  if (!planLimits) return false

  const maxAgents = planLimits.aiReceptionists
  return currentAgentCount < maxAgents
}

// Check if user has Nexus enterprise features
export function hasNexusEnterpriseFeatures(profile: Tables<"profiles"> | null): boolean {
  return profile?.nexus_plan === NEXUS_PLAN_ENTERPRISE && hasActiveNexusSubscription(profile)
}
